'use client';

/**
 * Leads board row — the `⋯` overflow menu (LF-5986 / LEADS-19).
 *
 * **Every item is an artefact action, never "set the status"**
 * (`functional-spec.md` §3.5.15). An earlier design offered `Move to next
 * status`, which contradicts the same principle that ruled out drag & drop:
 * a stage is *derived* from the artefacts that exist, so "move to next" would
 * have to write a stored stage without creating the thing that justifies it.
 * Sending the form or recording the meeting moves the lead; nothing here moves
 * it directly.
 *
 * **An item renders only when its handler is supplied.** The row is shipped
 * ahead of the mutation endpoints it will eventually drive (convert, close,
 * re-open and the intake-form send are each their own ticket), and a menu entry
 * that silently does nothing on click is worse than an absent one — so the menu
 * grows as its owners land, and the `⋯` trigger itself is replaced by a spacer
 * while nothing at all would render. That keeps the column's shared right edge
 * whether the trigger is there or not.
 */

import {
  BanIcon,
  CalendarPlusIcon,
  FolderOpenIcon,
  FolderPlusIcon,
  MoreHorizontalIcon,
  RotateCwIcon,
  SendIcon,
  Trash2Icon
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import type { LeadBoardRow } from '@/lib/services/leads';
import { LEAD_MENU_SLOT } from './lead-columns';
import { leadRowTitle } from './lead-row';

/** The row actions this menu can offer. Each is optional; absent means absent. */
export interface LeadRowActionHandlers {
  /** Opens the meeting form — the Meeting cell and this menu share it. */
  onEditMeeting?: () => void;
  /** Opens the intake-form send flow. */
  onSendForm?: () => void;
  /** Creates a matter from this lead (convert). */
  onCreateMatter?: () => void;
  /** Navigates to the already-linked matter. */
  onOpenMatter?: () => void;
  /** Opens the mandatory reason picker, then closes the lead. */
  onNotProceeding?: () => void;
  /** Re-opens a closed lead. */
  onReopen?: () => void;
  /**
   * Opens the delete confirmation (VALE-1616).
   *
   * Offered on **every** tab, unlike the other items here: the enquiries a firm
   * most wants gone are the test ones it already closed as `Not proceeding`,
   * which is precisely what pollutes the tab that should answer "what work did
   * we lose?".
   */
  onDelete?: () => void;
}

/**
 * Whether this row may offer `Delete enquiry`.
 *
 * **Omitted, not disabled, once the enquiry has a matter** — the shipped
 * Documents-card rule, for the same reason: an action the server will always
 * refuse is not an action, and a greyed row invites a click that can only ever
 * produce an error. Deleting would blank the matter's record of where it came
 * from, which is the one outcome this feature must never produce.
 *
 * `matterId` is the stored FK rather than a live-matter check, so an enquiry
 * whose matter was later soft-deleted keeps its affordance hidden even though
 * the server would now allow the delete. That is the conservative direction (it
 * never offers an action that fails) and it matches what the Documents card
 * already does with the same field.
 */
function canDelete(
  row: Pick<LeadBoardRow, 'matterId'>,
  handlers: LeadRowActionHandlers
): boolean {
  return !!handlers.onDelete && !row.matterId;
}

/**
 * The ellipsis is load-bearing, as it is on `Not proceeding…`: this opens a
 * confirmation rather than deleting outright.
 */
function deleteItem(onDelete: () => void) {
  return (
    <DropdownMenuItem
      key="delete"
      onClick={onDelete}
      className="text-error-subtle-foreground focus:text-error-subtle-strong-foreground"
    >
      <Trash2Icon className="mr-2 h-3.5 w-3.5" />
      Delete enquiry…
    </DropdownMenuItem>
  );
}

export function LeadRowActions({
  row,
  handlers
}: {
  row: Pick<
    LeadBoardRow,
    'deceasedName' | 'clientNames' | 'outcome' | 'matterId' | 'nextMeetingAt'
  >;
  handlers: LeadRowActionHandlers;
}) {
  const items = row.outcome
    ? closedItems(row, handlers)
    : openItems(row, handlers);

  // Nothing to offer — reserve the footprint so the columns keep one right edge.
  if (items.length === 0) {
    return <span className={LEAD_MENU_SLOT} aria-hidden />;
  }

  return (
    <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={`Actions for ${leadRowTitle(row)}`}
            className="text-foreground-subtle hover:bg-muted hover:text-foreground-secondary data-[state=open]:bg-muted cursor-pointer rounded p-1 transition-colors"
          >
            <MoreHorizontalIcon className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        {/*
          `w-52`, which is what made `Not proceeding…` the right label: the
          longer `Mark not proceeding…` wrapped to two lines here, and the fix
          was the rename rather than a wider menu because the long form was a
          second name for an action the drawer already called `Not proceeding…`.
        */}
        <DropdownMenuContent align="end" className="w-52">
          {items}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/**
 * An open lead: arrange the meeting, send or chase the form, take it to a
 * matter, or close it with a reason.
 *
 * **There is no `Pause` / `Resume follow-ups` entry.** The spec lists one, but
 * the shipped board row carries no chase-pause field — the per-lead pause
 * belongs to the chase engine (LEADS-39 / LF-6006) and has no column to read
 * yet. Rendering a toggle over state that does not exist would be a stub, so
 * the entry lands with its owner.
 */
function openItems(
  row: Pick<LeadBoardRow, 'matterId' | 'nextMeetingAt'>,
  handlers: LeadRowActionHandlers
) {
  const items = [];

  if (handlers.onEditMeeting) {
    items.push(
      // Not "Book meeting" / "Reschedule": nothing here books anything.
      // Calendar integration is deliberately out of scope, so what this opens is
      // a form recording a meeting she arranged on the phone. The *status* may
      // still read `Booked` — that is a true fact about the world; the *action*
      // has to describe what the software does.
      <DropdownMenuItem key="meeting" onClick={handlers.onEditMeeting}>
        <CalendarPlusIcon className="mr-2 h-3.5 w-3.5" />
        {row.nextMeetingAt ? 'Edit meeting details' : 'Add meeting details'}
      </DropdownMenuItem>
    );
  }

  if (handlers.onSendForm) {
    items.push(
      <DropdownMenuItem key="form" onClick={handlers.onSendForm}>
        <SendIcon className="mr-2 h-3.5 w-3.5" />
        Send intake form
      </DropdownMenuItem>
    );
  }

  const matterAction = row.matterId
    ? handlers.onOpenMatter
    : handlers.onCreateMatter;
  if (matterAction) {
    if (items.length > 0) {
      items.push(<DropdownMenuSeparator key="sep-matter" />);
    }
    items.push(
      <DropdownMenuItem key="matter" onClick={matterAction}>
        {row.matterId ? (
          <FolderOpenIcon className="mr-2 h-3.5 w-3.5" />
        ) : (
          <FolderPlusIcon className="mr-2 h-3.5 w-3.5" />
        )}
        {row.matterId ? 'Open linked matter' : 'Create matter'}
      </DropdownMenuItem>
    );
  }

  if (handlers.onNotProceeding) {
    items.push(
      // The ellipsis is load-bearing: a reason code is mandatory, so this opens
      // a picker rather than closing the lead outright.
      <DropdownMenuItem
        key="not-proceeding"
        onClick={handlers.onNotProceeding}
        className="text-error-subtle-foreground focus:text-error-subtle-strong-foreground"
      >
        <BanIcon className="mr-2 h-3.5 w-3.5" />
        Not proceeding…
      </DropdownMenuItem>
    );
  }

  if (canDelete(row, handlers) && handlers.onDelete) {
    // Last, and separated: `Not proceeding…` is a pipeline decision about real
    // work, deleting is a statement that this was never an enquiry at all.
    // Sitting them adjacent and identically styled is how a misclick picks the
    // irreversible one.
    if (items.length > 0) {
      items.push(<DropdownMenuSeparator key="sep-delete" />);
    }
    items.push(deleteItem(handlers.onDelete));
  }

  return items;
}

/**
 * A closed lead: view the matter it became, and re-open it. Nothing else — a
 * closed lead has no artefacts left to arrange.
 */
function closedItems(
  row: Pick<LeadBoardRow, 'outcome' | 'matterId'>,
  handlers: LeadRowActionHandlers
) {
  const items = [];
  const showMatter = !!(
    row.outcome === 'proceeding' &&
    row.matterId &&
    handlers.onOpenMatter
  );
  const showReopen = !!handlers.onReopen;

  if (showMatter) {
    items.push(
      <DropdownMenuItem key="matter" onClick={handlers.onOpenMatter}>
        <FolderOpenIcon className="mr-2 h-3.5 w-3.5" />
        View new matter
      </DropdownMenuItem>
    );
    // Only when something follows it — a trailing separator is a visible defect.
    if (showReopen) items.push(<DropdownMenuSeparator key="sep-reopen" />);
  }

  if (showReopen) {
    items.push(
      <DropdownMenuItem key="reopen" onClick={handlers.onReopen}>
        <RotateCwIcon className="mr-2 h-3.5 w-3.5" />
        Re-open lead
      </DropdownMenuItem>
    );
  }

  if (canDelete(row, handlers) && handlers.onDelete) {
    if (items.length > 0) {
      items.push(<DropdownMenuSeparator key="sep-delete" />);
    }
    items.push(deleteItem(handlers.onDelete));
  }

  return items;
}
